import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lock, UserRound } from 'lucide-react';
import { Selo, type CorSelo } from '../../componentes/base';
import { Selecao } from '../../componentes/formulario';
import { get } from '../../nucleo/api';
import { nomePessoa, textoLivre } from '../../nucleo/formato';
import { rotuloEntidade, rotuloModulo } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import { useSinal } from '../../nucleo/tempo-real';
import { i18n } from './i18n';
import type { Coluna, Quadro } from './tipos';

export const CHAVE = ['tarefas'] as const;

export const COR_PRIORIDADE: Record<string, CorSelo> = { P1: 'perigo', P2: 'laranja', P3: 'azul', P4: 'neutro' };

/** Nome da coluna de fábrica no idioma do app enquanto o nome gravado (pt) não mudou; nome trocado aparece como está. */
export function useRotuloColuna() {
  const { t } = useTranslation('tarefas');
  return (c: { nome: string; codigo: string | null }) => {
    const fabrica = c.codigo ? (i18n.pt.colunasPadrao as Record<string, string>)[c.codigo] : undefined;
    return fabrica && fabrica === c.nome ? t(`colunasPadrao.${c.codigo}`) : c.nome;
  };
}

export function useRotuloQuadro() {
  const { t } = useTranslation('tarefas');
  return (q: { nome: string; codigo: string | null }) => {
    const fabrica = q.codigo ? (i18n.pt.quadrosPadrao as Record<string, string>)[q.codigo] : undefined;
    return fabrica && fabrica === q.nome ? t(`quadrosPadrao.${q.codigo}`) : q.nome;
  };
}

export function useQuadros() {
  return useQuery({ queryKey: [...CHAVE, 'quadros'], queryFn: () => get<{ itens: Quadro[] }>('/tarefas/quadros') });
}

/** Sinal `tarefas` do SSE (W3): outra pessoa ou o agente mexeu num card; recarrega as consultas do módulo na hora. */
export function useTempoRealTarefas() {
  const qc = useQueryClient();
  useSinal('tarefas', () => void qc.invalidateQueries({ queryKey: CHAVE }));
}

/** Quadro escolhido na URL (`?quadro=`), com o de demandas como padrão. A escolha fica no link, para compartilhar. */
export function useQuadroEscolhido(quadros: Quadro[] | undefined): [string | null, (id: string) => void] {
  const [params, setParams] = useSearchParams();
  const pedido = params.get('quadro');
  const valido = quadros?.some((q) => q.id === pedido) ? pedido : null;
  const padrao = quadros?.find((q) => q.codigo === 'demandas')?.id ?? quadros?.[0]?.id ?? null;
  const atual = valido ?? padrao;
  const escolher = (id: string) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set('quadro', id);
        n.delete('card');
        return n;
      },
      { replace: true },
    );
  // Normaliza a URL quando o pedido não vale (quadro arquivado, link antigo).
  useEffect(() => {
    if (quadros && pedido && !valido && padrao) escolher(padrao);
  }, [quadros, pedido, valido, padrao]); // eslint-disable-line react-hooks/exhaustive-deps
  return [atual, escolher];
}

/** Fase 2 (W23, K11): registro de outro módulo a que o card novo nasce ligado ("Criar tarefa" no chatter). */
export interface VinculoNovo {
  entidade: string;
  id: string;
  titulo: string | null;
  link: string | null;
}

/** Entidades cujo título é nome de pessoa ou empresa: no modo discreto, as iniciais (`nomePessoa`). */
const ENTIDADES_DE_NOME = new Set(['cadastros.parceiro', 'compras.fornecedor', 'pessoas.funcionario']);

/**
 * O registro vinculado como texto: "Parceiro (Cadastros): J. S.". Sem título, só o tipo do registro. O título é texto do
 * usuário: nome de pessoa pelas iniciais com a opção de nomes, o resto pelos padrões do modo discreto (`textoLivre`).
 */
export function textoDoVinculo(entidade: string, titulo: string | null | undefined) {
  const tipo = rotuloEntidade(entidade);
  if (!titulo?.trim()) return tipo;
  return `${tipo}: ${ENTIDADES_DE_NOME.has(entidade) ? nomePessoa(titulo) : textoLivre(titulo)}`;
}

/** Card aberto na ficha (`?card=`). */
export function useCardAberto(): [string | null, (id: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const abrir = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('card', id);
        else n.delete('card');
        return n;
      },
      { replace: true },
    );
  return [params.get('card'), abrir];
}

export function SeletorQuadro({ quadros, valor, aoMudar }: { quadros: Quadro[]; valor: string | null; aoMudar: (id: string) => void }) {
  const { t } = useTranslation('tarefas');
  const rotulo = useRotuloQuadro();
  return (
    <label className="flex w-full flex-col gap-1 text-xs text-tinta-3 sm:w-64">
      {t('campos.quadro')}
      <Selecao aria-label={t('campos.quadro')} opcoes={quadros.map((q) => ({ valor: q.id, rotulo: rotulo(q) }))} value={valor ?? ''} onChange={(e) => aoMudar(e.target.value)} />
    </label>
  );
}

/** Módulos que podem ser alvo de um card: os instalados, mais Núcleo e Novo módulo. */
export function useOpcoesModuloAlvo() {
  const { t } = useTranslation('tarefas');
  const s = useSessao();
  return [
    ...s.modulos.map((m) => ({ valor: m.slug, rotulo: rotuloModulo(m.slug, m.nome) })).sort((a, b) => a.rotulo.localeCompare(b.rotulo)),
    { valor: 'nucleo', rotulo: t('alvos.nucleo') },
    { valor: 'novo', rotulo: t('alvos.novo') },
  ];
}

export function useRotuloModuloAlvo() {
  const { t } = useTranslation('tarefas');
  return (slug: string | null) => (!slug ? '' : slug === 'nucleo' || slug === 'novo' ? t(`alvos.${slug}`) : rotuloModulo(slug));
}

/** Selos da coluna: exige aprovação (cadeado) e espera decisão (pessoa). Cor e ícone, nunca só a cor. */
export function SelosColuna({ c }: { c: Pick<Coluna, 'exige_aprovacao' | 'espera_pessoa'> }) {
  const { t } = useTranslation('tarefas');
  return (
    <>
      {c.exige_aprovacao && (
        <Selo cor="roxo">
          <Lock className="mr-1 size-3" aria-hidden />
          {t('kanban.aprovacao')}
        </Selo>
      )}
      {c.espera_pessoa && (
        <Selo cor="laranja">
          <UserRound className="mr-1 size-3" aria-hidden />
          {t('kanban.esperaPessoa')}
        </Selo>
      )}
    </>
  );
}

/**
 * Colunas de aprovação que o movimento atravessa para a frente (a mesma regra do servidor, `aprovacoesNoCaminho`):
 * a tela só desabilita o botão para quem não pode; quem decide é sempre o servidor.
 */
export function atravessaAprovacao(colunas: Pick<Coluna, 'id' | 'ordem' | 'exige_aprovacao'>[], origem: string, destino: string): boolean {
  const ord = [...colunas].sort((a, b) => a.ordem - b.ordem || a.id.localeCompare(b.id));
  const i = ord.findIndex((c) => c.id === origem);
  const j = ord.findIndex((c) => c.id === destino);
  return j > i && ord.slice(i + 1, j + 1).some((c) => c.exige_aprovacao);
}
